/* eslint-disable @typescript-eslint/no-explicit-any */
import { useState } from 'react'
import RequestLoginModalStyled from './styled'
import { Modal } from 'shared'
import { useTranslation } from 'react-i18next'
import { NOTIFICATION_MESSAGE } from 'i18n/constants'
import { METAMASK_LOGIN_BTN, TOKENPOCKET_LOGIN_BTN } from 'assets/images'
import authApi from 'apis/authApi'
import userApi from 'apis/userApi'
import { configs, constants, MESSAGE } from 'apps'
import { setAuthState } from 'features/Auth/auth.slice'
import { setCurrentUser } from 'features/Profile/profile.slice'
import { ResultHorseModal } from 'features/Race/components'
import ConfirmOkModal from 'features/Race/components/ConfirmOkModal'
import { useAppDispatch, useLocalStorage, useToggle } from 'hooks'
import { AuthState, PostLoginError } from 'models'
import { handleAsyncRequest } from 'utils/helper'
import {
  getBalance,
  getCurrentUser,
  getPostLoginBody,
  getRandomMessage,
  getSignerByProvider,
  isRequestAccountsError
} from 'utils/metamask'
import { useAvailableWallet } from 'features/Auth/pages/Main/useAvailableWallet'
import { EIP6963ProviderDetail } from 'features/Auth/pages/Main/types'

interface RequestLoginModalProps {
  toggleIsModalOpen: (value: boolean) => void
}

function RequestLoginModal({ toggleIsModalOpen }: RequestLoginModalProps) {
  const providers = useAvailableWallet()
  const [errorMessage, setErrorMessage] = useState<string>('')
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [, setAccessToken] = useLocalStorage(constants.ACCESS_TOKEN_KEY, null)
  const [, setRefreshToken] = useLocalStorage(constants.REFRESH_TOKEN_KEY, null)
  const [, setStorageUserId] = useLocalStorage(constants.USER_ID_KEY, 0)
  const dispatch = useAppDispatch()
  const [flagMetamask, setFlagMetamask] = useState<boolean>(false)
  const [flagTokenPocket, setFlagTokenPocket] = useState<boolean>(false)
  const [isModalResultHorseOpen, toggleIsModalResultHorseOpen] = useToggle(false)
  const [isSyncHorseModal, toggleSyncHorseModal] = useToggle(false)
  const { t } = useTranslation()

  const handleOverlayClick = () => {
    toggleIsModalOpen(false)
  }

  const fetchSyncHorse = async () => {
    await handleAsyncRequest(userApi.getSyncHorse({ loginFlag: 1 }))
    toggleIsModalOpen(false)
    window.location.reload()
  }

  const handleCloseSyncHorse = () => {
    toggleIsModalOpen(false)
    window.location.reload()
  }

  const isPostLoginError = (candidate: any): candidate is PostLoginError => {
    return candidate.code === constants.HTTP_STATUS.BAD_REQUEST && typeof candidate.message === 'string'
  }

  const handleCloseModalResult = () => {
    toggleIsModalResultHorseOpen(false)
    setFlagMetamask(false)
    setFlagTokenPocket(false)
  }

  const handleConnect = async (providerWithInfo: EIP6963ProviderDetail) => {
    const accounts: string[] | undefined = (await providerWithInfo.provider
      .request({ method: 'eth_requestAccounts' })
      .catch(console.error)) as string[] | undefined

    if (!accounts?.[0]) return

    localStorage.setItem('walletName', providerWithInfo.info?.name)
    const ethereum = providerWithInfo.provider

    if (isLoading) return
    setIsLoading(true)

    const chainID = await ethereum.request({ method: 'eth_chainId' }) ?? ''
    if (!chainID) {
      setIsLoading(false)
      return
    }

    const currentChainId = providerWithInfo.info?.name === 'MetaMask'
      ? String(parseInt(chainID as string, 16))
      : String(parseInt(ethereum.chainId as string, 16))

    if (currentChainId === String(configs.chainId)) {
      localStorage.removeItem(constants.CLICKLOGIN)
      const [signerError, signer] = await handleAsyncRequest(getSignerByProvider(ethereum))
      if (signerError && isRequestAccountsError(signerError)) {
        ethereum.request({
          method: 'wallet_requestPermissions',
          params: [{ eth_accounts: {} }]
        })
        setErrorMessage(providerWithInfo.info?.name === 'MetaMask'
          ? 'Please login MetaMask'
          : 'Please login TokenPocket')
        setIsLoading(false)
        return
      }
      if (!signer) { setIsLoading(false); return }

      const [, randomMessage] = await handleAsyncRequest(getRandomMessage())
      if (!randomMessage) { setIsLoading(false); return }

      const [loginError, postLoginBody] = await handleAsyncRequest(getPostLoginBody(signer, randomMessage))
      if (loginError && isRequestAccountsError(loginError)) { setIsLoading(false); return }
      if (!postLoginBody) { setIsLoading(false); return }

      const [postLoginError, loginResponse] = await handleAsyncRequest(authApi.postLogin(postLoginBody))
      if (postLoginError && isPostLoginError(postLoginError)) {
        setErrorMessage(postLoginError.message)
      }
      if (!loginResponse) { setIsLoading(false); return }

      const { data: { access_token, refresh_token, user_id } } = loginResponse
      setAccessToken(access_token)
      setRefreshToken(refresh_token)
      setStorageUserId(user_id)

      const [, currentUser] = await handleAsyncRequest(getCurrentUser())
      if (!currentUser) { setIsLoading(false); return }

      const [, balance] = await handleAsyncRequest(getBalance(signer))
      if (typeof balance !== 'string') { setIsLoading(false); return }

      const valueMARE = balance.split(',').shift()
      const valueEMAS = balance.split(',').pop()

      const authState: AuthState = {
        user_id,
        isLogged: true,
        isFirstLogin: true,
        balance: { coinMare: valueMARE, coinEmas: valueEMAS }
      }

            dispatch(setAuthState(authState))
      dispatch(setCurrentUser(currentUser))
      localStorage.setItem('walletAddress', accounts[0])
      toggleSyncHorseModal(true)
    } else {
      try {
        await ethereum.request({
          method: 'wallet_addEthereumChain',
          params: [{
            chainId: configs.chainIdDirect,
            rpcUrls: [configs.linkRpcUrls],
            chainName: configs.nameChain,
            nativeCurrency: {
              name: configs.nameCurrency,
              symbol: configs.nameCurrency,
              decimals: +configs.valueDecimals
            },
            blockExplorerUrls: [configs.linkBlockExplorerUrls]
          }]
        })
      } catch (addError) {
        console.log(addError, 'addError')
      }
    }
    setIsLoading(false)
  }

  const checkWallet = (walletType: string) => () => {
    const myProvider = providers.find((prov) => prov.info?.name === walletType)
    if (!myProvider) {
      if (walletType === 'MetaMask') {
        setFlagMetamask(true)
      } else {
        setFlagTokenPocket(true)
      }
      toggleIsModalResultHorseOpen(true)
    } else {
      handleConnect(myProvider)
    }
  }

  return (
    <Modal onOverlayClick={handleOverlayClick}>
      <RequestLoginModalStyled>
        <div className='request-login-modal d-flex flex-column justify-content-between'>
          <p className='color-white text-center font-size-20 mt-4'>
            {t(`${NOTIFICATION_MESSAGE}.pleaseLogin`)}
          </p>
          <div className='login-container'>
            <div className='login-wrapper d-flex flex-column flex-md-row justify-content-center align-items-center'>
              <button
                className='login-btn p-0'
                onClick={checkWallet('MetaMask')}
                disabled={isLoading}
              >
                <img src={METAMASK_LOGIN_BTN} alt='MetaMask Login' className='login-img' />
              </button>
              <button
                className='login-btn p-0'
                onClick={checkWallet('TokenPocket')}
                disabled={isLoading}
              >
                <img src={TOKENPOCKET_LOGIN_BTN} alt='TokenPocket Login' className='login-img' />
              </button>
            </div>
          </div>
          {errorMessage && (
            <div className='err-container color-red text-center'>{errorMessage}</div>
          )}
          <div className='btn-container d-flex justify-content-center mt-3'>
            <button className='cancel-btn' onClick={handleOverlayClick}>
              <span className='color-primary font-bold'>{t(`${NOTIFICATION_MESSAGE}.cancel`)}</span>
            </button>
          </div>
        </div>
      </RequestLoginModalStyled>
      {isModalResultHorseOpen && (
        <ResultHorseModal
          title={'warning'}
          onCloseButtonClick={handleCloseModalResult}
          onOk={handleCloseModalResult}
          message={flagMetamask ? MESSAGE.please_install_metamask : flagTokenPocket ? MESSAGE.please_install_token_pocket : MESSAGE.please_install_spectre}
        />
      )}
      {isSyncHorseModal && (
        <ConfirmOkModal
          onCloseButtonClick={handleCloseSyncHorse}
          onConfirm={fetchSyncHorse}
          message={
            <div className='font-size-18'>
              <div dangerouslySetInnerHTML={{ __html: t(`${NOTIFICATION_MESSAGE}.syncHorseNow`) }} />
            </div>
          }
          title='Warning'
        />
      )}
    </Modal>
  )
}

export default RequestLoginModal
